import { type FC, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAsyncFn, useDebounce } from 'react-use';
import { type AsyncState } from 'react-use/lib/useAsyncFn';
import styled from 'styled-components';

import { Skeleton, Text, colors } from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import {
  type Suggestion,
  getPlacePredictions,
  latLongToPostalCode,
} from '@service-initiation/shared-data-access-google';
import { ClickableText } from '@service-initiation/shared-ui-clickable-text';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  DeliveryMethod,
  type Geolocation,
  type Location,
  type ParsedStore,
  deliverySelectionConfirmedAtom,
  store,
  storeIdAtom,
  useDeliveryMethodAtom,
  useFullAddressAtom,
  useGeolocationAtom,
  usePostalCodeAtom,
  useSetAlertBannerAtom,
  validateSelectedStoreField,
} from '@service-initiation/tech-care-data-access-store';
import { ErrorType } from '@service-initiation/tech-care-data-access-tech-care-services';

import { useGeoLatLong } from '../../hooks/useGeoLatLong';
import { useLocationSelect } from '../../hooks/useLocationSelect';
import { useStores } from '../../hooks/useStores';
import Drawer from './components/Drawer';
import StoresMap from './components/StoresMap';
import { ZipCodeForm } from './zip-code/components/ZipCodeForm';
import { validateZipcode } from './zip-code/validateZipCode';

const TEXT = TranslationEntries.Repairs.StoreLocator;

const StoreSubTextMainContainer = styled.div`
  margin-top: 16px;
  max-width: 250px;
`;

const StoreSubTextSecondaryContainer = styled.div`
  max-width: 375px;
`;

const SecondarySkeleton = styled(Skeleton)`
  margin-top: 8px;
`;

const StoreZoomEnum = {
  InitialZoom: 3,
  NoStoresZoom: 5,
  StoresZoom: 9,
};

const DEFAULT_SELECTED_STORE = { selectedStore: undefined };

const NO_RESULTS = 'INVALID';
const GEOLOCATION_PRECISION = 3; // Number of decimals to keep
const ZIP_CODE_TEXT = TranslationEntries.Repairs.ZipCode;

export const StoreSelectionTab: FC = () => {
  const { dispatchClickEvent, dispatchViewEvent } = useAnalytics();
  const [{ loading: loadingNextStep }, goToNextStep] = useGoToNextStep();
  const [fullAddress, setFullAddress] = useFullAddressAtom();
  const [geolocation, setGeolocation] = useGeolocationAtom();
  const [postalCode, setPostalCode] = usePostalCodeAtom();
  const [, setDeliveryMethod] = useDeliveryMethodAtom();
  const { loading: isLoading, stores = [] } = useStores();
  const [inputText, setInputText] = useState(
    postalCode || fullAddress?.address || ''
  );

  const [predictions, setPredictions] = useState<Suggestion[]>();
  const [geolocationPositionError, setGeolocationPositionError] = useState(0);

  const [evaluateGeolocation, setEvaluateGeolocation] = useState(false);
  const [validationError, setValidationError] = useState<ErrorType | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const setAlertBanner = useSetAlertBannerAtom();

  const [, updateAddressComponents] = useAsyncFn(
    async (addressComponents: Location) => {
      setGeolocation(addressComponents.geoLocation);
      setFullAddress(addressComponents.fullAddress);
      setPostalCode(addressComponents.postalCode);
    },
    [setGeolocation, setFullAddress, setPostalCode]
  );

  const updatePredictions = useCallback(
    async (newInputText: string) => {
      if (newInputText) {
        const res = await getPlacePredictions(newInputText);
        if (res?.predictions.length === 0 || res === undefined) {
          setPredictions([
            { header: 'No Results for that location', id: NO_RESULTS },
          ] as Suggestion[]);
          dispatchClickEvent('StoreSelectionInvalidInput', {
            Cta: 'Done',
            addressInput: newInputText,
          });
        } else {
          const formattedPreds = res?.predictions
            .filter((pred) => pred.description.split(',').length > 2)
            .map((prediction) => {
              return {
                header: prediction.structured_formatting.main_text,
                id: prediction.place_id,
                subHeader: prediction.structured_formatting.secondary_text,
                value: prediction.description,
              } as Suggestion;
            });
          setPredictions(formattedPreds);
        }
      }
    },
    [dispatchClickEvent]
  );

  useDebounce(
    async () => {
      await updatePredictions(inputText);
    },
    375,
    [updatePredictions, inputText]
  );

  const handleTextChange = useCallback(async (input: string) => {
    setValidationError(undefined);
    setInputText(input);
  }, []);

  const [state, onLocationSelect] = useLocationSelect();

  const handleShowInvalidCountryAlertBanner = useCallback(() => {
    setAlertBanner({
      bannerType: 'warn',
      message: t(ZIP_CODE_TEXT.ZipCodeAlertBannerInvalidCountry),
    });
    dispatchViewEvent('AlertBanner', {
      message: t(ZIP_CODE_TEXT.ZipCodeAlertBannerInvalidCountry),
    });
  }, [dispatchViewEvent, setAlertBanner, t]);

  const validateGeoLocation = useCallback(
    async (geoLocation: Geolocation) => {
      const { error, isValid } = await validateZipcode({
        geoLocation,
      });

      if (isValid) {
        validationError && setValidationError(undefined);
        const parsedZip = await latLongToPostalCode(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          geoLocation!.latitude,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          geoLocation!.longitude
        );
        parsedZip && setInputText(parsedZip);
        await updateAddressComponents({
          fullAddress: undefined,
          geoLocation,
          postalCode: parsedZip,
        });
      } else {
        if (error === ErrorType.InvalidCountry) {
          handleShowInvalidCountryAlertBanner();
        } else {
          setValidationError(error);
        }
      }

      setEvaluateGeolocation(false);
    },
    [
      handleShowInvalidCountryAlertBanner,
      updateAddressComponents,
      validationError,
    ]
  );

  const updateGeoLocation: PositionCallback = useCallback(
    (position) => {
      let { latitude, longitude } = position.coords;
      let geoLocation: Geolocation;
      try {
        // To avoid PII (a fine tuned customer location), cut off some decimals from geolocation
        const geoScaler = Math.pow(10, GEOLOCATION_PRECISION);
        latitude = Math.trunc(latitude * geoScaler) / geoScaler;
        longitude = Math.trunc(longitude * geoScaler) / geoScaler;
        geoLocation = { latitude, longitude };
        dispatchClickEvent('GeoLocationPrompt', {
          Cta: 'Use current location',
          Status: 'Approved',
        });
        if (geoLocation) {
          void validateGeoLocation(geoLocation);
        }
      } catch (_) {
        setEvaluateGeolocation(false);
      }
    },
    [dispatchClickEvent, validateGeoLocation]
  );

  const geolocationError: PositionErrorCallback = useCallback(
    (error) => {
      setEvaluateGeolocation(false);
      setGeolocationPositionError(error.code);
      dispatchClickEvent('GeoLocationPrompt', {
        Cta: 'Use current location',
        Status: 'Blocked',
      });
    },
    [dispatchClickEvent]
  );

  const showGeolocationPrompt = useCallback(async () => {
    if (navigator.geolocation) {
      setInputText('');
      setEvaluateGeolocation(true);
      navigator.geolocation.getCurrentPosition(
        updateGeoLocation,
        geolocationError
      );
    }
  }, [updateGeoLocation, geolocationError]);

  const { loading: geoCodingStateLoading, value: alternativeLatLng } =
    useGeoLatLong();

  const [focusedStore, setFocusedStore] = useState<ParsedStore>();

  const isExpanded = !!focusedStore;

  const extraAnalyticsData = useMemo(() => {
    return {
      MapView: isExpanded ? 'ExtendedDetails' : 'LocationOnly',
      NumberOfStores: stores.length ?? 0,
    };
  }, [isExpanded, stores.length]);

  const { ref: elementRef } = useInViewAnalytics(
    'StoreSelectionTab',
    extraAnalyticsData
  );

  const handleStoreClick = useCallback(
    async (selectedStore: ParsedStore) => {
      dispatchClickEvent('SelectStore', {
        Cta: selectedStore.name,
        ...extraAnalyticsData,
      });

      store.set(storeIdAtom, selectedStore.id);
      store.set(deliverySelectionConfirmedAtom, true);
      if (
        validateSelectedStoreField(
          selectedStore ? selectedStore : DEFAULT_SELECTED_STORE.selectedStore
        )
      ) {
        goToNextStep();
      }
    },
    [dispatchClickEvent, extraAnalyticsData, goToNextStep]
  );

  const storeMapZoom = useMemo(() => {
    if (stores.length === 0 && (postalCode || fullAddress || geolocation)) {
      return StoreZoomEnum.NoStoresZoom;
    } else if (
      stores.length === 0 &&
      !postalCode &&
      !fullAddress &&
      !geolocation
    ) {
      return StoreZoomEnum.InitialZoom;
    } else {
      return StoreZoomEnum.StoresZoom;
    }
  }, [stores.length, postalCode, fullAddress, geolocation]);
  return (
    <div ref={elementRef}>
      <ZipCodeForm
        evaluateGeolocation={evaluateGeolocation}
        geolocationPositionError={geolocationPositionError}
        handleTextChange={handleTextChange}
        inputText={inputText}
        isLoading={isLoading}
        onLocationSelect={(place) =>
          onLocationSelect({
            place,
            setGeolocationPositionError,
            setInputText,
            setValidationError,
            updateAddressComponents,
          })
        }
        predictions={predictions}
        setInputText={setInputText}
        showGeolocationPrompt={showGeolocationPrompt}
        state={state as AsyncState<boolean>}
        validationError={validationError}
      />
      <StoresMap
        alternativeLatLng={alternativeLatLng || { lat: 0, lng: 0 }}
        evaluateGeolocation={evaluateGeolocation}
        isLoading={!!isLoading || geoCodingStateLoading}
        setFocusedStore={setFocusedStore}
        stores={stores}
        zoom={storeMapZoom}
      />
      {(postalCode || fullAddress || geolocation) && (
        <>
          <StoreSubTextMainContainer>
            {!(evaluateGeolocation || isLoading) ? (
              <Trans
                components={{
                  Text: <Text size={3} weight="base" />,
                  b: <Text color="black" size={3} weight="heavy" />,
                }}
                i18nKey={
                  stores.length > 0
                    ? TEXT.VisitStore.Title
                    : TEXT.VisitStore.NoStoresTitle
                }
                values={{
                  numOfStores: stores.length,
                  pluralSuffix: stores.length === 1 ? '' : 's',
                }}
              />
            ) : (
              <Skeleton height={'24px'} />
            )}
          </StoreSubTextMainContainer>
          <StoreSubTextSecondaryContainer>
            {!(evaluateGeolocation || isLoading) ? (
              <Trans
                components={{
                  FirstUnderline: (
                    <ClickableText
                      color={colors.neutralDeepest}
                      onClick={() => {
                        setDeliveryMethod(DeliveryMethod.MailIn);
                      }}
                    />
                  ),
                  SecondUnderline: (
                    <ClickableText
                      color={colors.neutralDeepest}
                      onClick={() => {
                        setInputText && setInputText('');
                        setGeolocation(undefined);
                        setFullAddress(undefined);
                        setPostalCode(undefined);
                      }}
                    />
                  ),
                  Text: <Text color={colors.neutralDeepest} weight="feather" />,
                }}
                i18nKey={
                  !(stores.length > 0) ? TEXT.VisitStore.NoStoresSubtitle : ''
                }
              />
            ) : (
              <SecondarySkeleton height={'24px'} />
            )}
          </StoreSubTextSecondaryContainer>
        </>
      )}
      {stores.length > 0 && (
        <Drawer
          evaluateGeolocation={evaluateGeolocation}
          focusedStore={focusedStore}
          handleStoreClick={handleStoreClick}
          isLoading={!!isLoading || loadingNextStep}
          stores={stores}
        />
      )}
    </div>
  );
};
